import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminSchedulers = () => {
  const navigate = useNavigate();
  const [schedulers, setSchedulers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [editingScheduler, setEditingScheduler] = useState(null);
  const [newCron, setNewCron] = useState('');
  const [savingCron, setSavingCron] = useState(false);
  const [tboStatus, setTboStatus] = useState(null);
  const [tboCityStatus, setTboCityStatus] = useState(null);
  const [tripJackStatus, setTripJackStatus] = useState(null);
  const [tripJackCityStatus, setTripJackCityStatus] = useState(null);
  const [rezLiveCityStatus, setRezLiveCityStatus] = useState(null);
  const [rezLiveHotelStatus, setRezLiveHotelStatus] = useState(null);

  useEffect(() => {
    fetchSchedulers();
    fetchTboStatus();
    fetchTboCityStatus();
    fetchTripJackStatus();
    fetchTripJackCityStatus();
    fetchRezLiveCityStatus();
    fetchRezLiveHotelStatus();
  }, []);

  const fetchTboStatus = async () => {
    try {
      const res = await adminApiService.getSchedulerStatus('tboHotelStaticContentSync');
      if (res.success) {
        setTboStatus(res);
      }
    } catch (err) {
      console.error('Error fetching TBO status:', err);
    }
  };

  const fetchTboCityStatus = async () => {
    try {
      const res = await adminApiService.getSchedulerStatus('tboCountryCitySync');
      if (res.success) {
        setTboCityStatus(res);
      }
    } catch (err) {
      console.error('Error fetching TBO Country City status:', err);
    }
  };

  const fetchTripJackStatus = async () => {
    try {
      const res = await adminApiService.getSchedulerStatus('tripJackHotelStaticContentSync');
      if (res.success) {
        setTripJackStatus(res);
      }
    } catch (err) {
      console.error('Error fetching TripJack status:', err);
    }
  };

  const fetchTripJackCityStatus = async () => {
    try {
      const res = await adminApiService.getSchedulerStatus('tripJackCityRegionMappingSync');
      if (res.success) {
        setTripJackCityStatus(res);
      }
    } catch (err) {
      console.error('Error fetching TripJack City Region status:', err);
    }
  };

  const fetchRezLiveCityStatus = async () => {
    try {
      const res = await adminApiService.getSchedulerStatus('rezLiveCitySync');
      if (res.success) {
        setRezLiveCityStatus(res);
      }
    } catch (err) {
      console.error('Error fetching RezLive City status:', err);
    }
  };

  const fetchRezLiveHotelStatus = async () => {
    try {
      const res = await adminApiService.getSchedulerStatus('rezLiveHotelSync');
      if (res.success) {
        setRezLiveHotelStatus(res);
      }
    } catch (err) {
      console.error('Error fetching RezLive Hotel status:', err);
    }
  };

  const fetchSchedulers = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getSchedulers();
      if (response.success) {
        setSchedulers(response.schedulers || []);
      } else {
        toast.error(response.message || 'Failed to fetch schedulers');
      }
    } catch (error) {
      console.error('Error fetching schedulers:', error);
      toast.error('Failed to load schedulers');
    } finally {
      setLoading(false);
    }
  };

  const handleRunImmediately = async (service, id, schedulerName) => {
    const displayName = schedulerName || id;
    const isConfirmed = window.confirm(`Are you sure you want to run the scheduler "${displayName}"?`);
    if (!isConfirmed) {
      return;
    }

    try {
      setRunningId(id);
      toast.info(`Triggering scheduler '${displayName}'...`);
      const response = await adminApiService.runScheduler(service, id);
      if (response.success) {
        toast.success(response.message || 'Scheduler run triggered successfully in the background!');
        // Refresh list to show updated status after a short delay
        setTimeout(fetchSchedulers, 2000);
      } else {
        toast.error(response.message || 'Failed to trigger scheduler');
      }
    } catch (error) {
      console.error('Error running scheduler:', error);
      toast.error('Failed to execute scheduler run');
    } finally {
      setRunningId(null);
    }
  };

  const handleResetStatus = async (service, id, name) => {
    if (!window.confirm(`Are you sure you want to reset status for scheduler '${name}'? This will clear stuck errors and reset state to READY.`)) {
      return;
    }
    try {
      const res = await adminApiService.resetSchedulerStatus(service, id);
      if (res.success) {
        toast.success(`Status for '${name}' reset successfully.`);
        fetchSchedulers();
        fetchTboStatus();
        fetchTboCityStatus();
        fetchTripJackStatus();
        fetchTripJackCityStatus();
        fetchRezLiveCityStatus();
        fetchRezLiveHotelStatus();
      } else {
        toast.error(res.message || 'Failed to reset status');
      }
    } catch (err) {
      console.error('Error resetting scheduler status:', err);
      toast.error(err.message || 'Failed to reset status');
    }
  };

  const startEditCron = (scheduler) => {
    setEditingScheduler(scheduler);
    setNewCron(scheduler.cron);
  };

  const cancelEditCron = () => {
    setEditingScheduler(null);
    setNewCron('');
  };

  const handleSaveCron = async (e) => {
    e.preventDefault();
    if (!newCron.trim()) {
      toast.error('Cron expression cannot be empty');
      return;
    }

    try {
      setSavingCron(true);
      const response = await adminApiService.updateSchedulerCron(
        editingScheduler.service,
        editingScheduler.id,
        newCron
      );

      if (response.success) {
        toast.success('Scheduler time updated successfully!');
        setEditingScheduler(null);
        fetchSchedulers();
      } else {
        toast.error(response.message || 'Failed to update scheduler cron');
      }
    } catch (error) {
      console.error('Error updating cron:', error);
      toast.error('An error occurred. Check cron format (must be a valid Spring 6-field Cron Trigger).');
    } finally {
      setSavingCron(false);
    }
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return 'Never';
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (e) {
      return dateTimeStr;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-500 font-medium">Loading Schedulers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/admin/dashboard/settings')}
            className="mr-4 p-2 text-gray-500 hover:text-gray-700 bg-white shadow-sm border border-gray-200 rounded-full hover:bg-gray-100 transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">System Schedulers</h1>
            <p className="text-gray-600 mt-1">Configure intervals and trigger background processes across applications</p>
          </div>
        </div>
        <button 
          onClick={fetchSchedulers}
          className="flex items-center space-x-2 py-2 px-4 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.5" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      {/* Schedulers Grid */}
      <div className="grid grid-cols-1 gap-6">
        {schedulers.map((scheduler) => (
          <div 
            key={scheduler.id} 
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
          >
            {(() => {
              const isRequestInFlight = runningId === scheduler.id;
              const isLiveInProgress = 
                scheduler.lastRunStatus === 'IN_PROGRESS' ||
                (scheduler.id === 'tboCountryCitySync' && tboCityStatus?.status === 'IN_PROGRESS') ||
                (scheduler.id === 'tripJackCityRegionMappingSync' && tripJackCityStatus?.status === 'IN_PROGRESS') ||
                (scheduler.id === 'rezLiveCitySync' && rezLiveCityStatus?.status === 'IN_PROGRESS') ||
                (scheduler.id === 'rezLiveHotelSync' && rezLiveHotelStatus?.status === 'IN_PROGRESS') ||
                (scheduler.id === 'tboHotelStaticContentSync' && tboStatus?.status === 'IN_PROGRESS') ||
                (scheduler.id === 'tripJackHotelStaticContentSync' && tripJackStatus?.status === 'IN_PROGRESS');

              const showResetButton = scheduler.lastRunStatus === 'IN_PROGRESS' || scheduler.lastRunStatus === 'FAILED';

              return (
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 pb-4 border-b border-gray-100 gap-4">
                  <div>
                    <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                      <h3 className="text-xl font-bold text-gray-900">{scheduler.name}</h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        scheduler.service === 'hotelapp' 
                          ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' 
                          : 'bg-teal-50 text-teal-700 border border-teal-100'
                      }`}>
                        {scheduler.service === 'hotelapp' ? 'hotelapp (Frontend)' : 'hotel-api (Backend)'}
                      </span>
                      {isLiveInProgress && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200 animate-pulse">
                          IN_PROGRESS
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-xs mt-1 font-mono bg-gray-50 px-2 py-1 rounded inline-block">
                      Key: {scheduler.id}
                    </p>
                  </div>

                  <div className="flex items-center space-x-3 w-full md:w-auto flex-wrap">
                    {showResetButton && (
                      <button
                        onClick={() => handleResetStatus(scheduler.service, scheduler.id, scheduler.name)}
                        className="flex-1 md:flex-none inline-flex items-center justify-center px-3 py-2 border border-rose-300 rounded-lg text-xs font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 shadow-sm transition-colors duration-150"
                        title="Clear stuck error or reset IN_PROGRESS status back to READY"
                      >
                        <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.5" />
                        </svg>
                        Reset Status
                      </button>
                    )}
                    <button
                      onClick={() => startEditCron(scheduler)}
                      className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors duration-150"
                    >
                      Edit Time
                    </button>
                    <button
                      onClick={() => handleRunImmediately(scheduler.service, scheduler.id, scheduler.name)}
                      disabled={isRequestInFlight}
                      className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                    >
                      {isRequestInFlight ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          Triggering...
                        </span>
                      ) : (
                        'Run Immediately'
                      )}
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Description & Config */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-sm">
              <div className="lg:col-span-2">
                <h4 className="font-semibold text-gray-700 mb-1">Description</h4>
                <p className="text-gray-600 leading-relaxed">{scheduler.description}</p>

                {/* TBO Hotel Static Content Sync Live Progress Card with See Progress button */}
                {scheduler.id === 'tboHotelStaticContentSync' && (tboStatus?.status === 'IN_PROGRESS' || scheduler.lastRunStatus === 'IN_PROGRESS') && (
                  <div className="mt-4 p-5 bg-slate-900 text-white rounded-xl border border-slate-800 shadow-lg">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-xs uppercase tracking-wider text-indigo-400 flex items-center">
                          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 inline-block mr-2 animate-ping"></span>
                          Live Execution Progress
                        </span>
                        <span className="text-xs font-mono bg-indigo-950 px-2 py-0.5 rounded text-indigo-300 border border-indigo-700/50">
                          {tboStatus?.phase || 'STEP1_HOTEL_CODES'}
                        </span>
                      </div>

                      <button
                        onClick={fetchTboStatus}
                        className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold shadow transition-all duration-150 active:scale-95"
                      >
                        <svg className="w-3.5 h-3.5 animate-spin-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.5" />
                        </svg>
                        <span>See Progress / Refresh</span>
                      </button>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-800 rounded-full h-5 overflow-hidden border border-slate-700 p-0.5">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          tboStatus?.status === 'IN_PROGRESS' 
                            ? 'bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-400 animate-pulse' 
                            : tboStatus?.status === 'SUCCESS' 
                            ? 'bg-emerald-500' 
                            : 'bg-indigo-600'
                        }`}
                        style={{ width: `${Math.min(100, Math.max(0, tboStatus?.progressPercentage || 0))}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between items-center mt-2 text-xs font-semibold">
                      <span className="text-slate-300">
                        Overall Completion: <strong className="text-emerald-400 text-sm">{tboStatus?.progressPercentage ? tboStatus.progressPercentage.toFixed(1) : 0}%</strong>
                      </span>
                      <span className="text-slate-400">
                        Status: <strong className={tboStatus?.status === 'IN_PROGRESS' ? 'text-amber-400 animate-pulse' : 'text-emerald-400'}>{tboStatus?.status || 'READY'}</strong>
                      </span>
                    </div>

                    {/* Phase Metrics Breakdown */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-3.5 pt-3 border-t border-slate-800 text-xs">
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Step 1 Cities</span>
                        <span className="font-extrabold text-white text-sm">{tboStatus?.processedCities || 0} / {tboStatus?.totalCities || 0}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Step 2 Batches</span>
                        <span className="font-extrabold text-white text-sm">{tboStatus?.processedDetailBatches || 0} / {tboStatus?.totalDetailBatches || 0}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Successful</span>
                        <span className="font-extrabold text-emerald-400 text-sm">{tboStatus?.completedDetailBatches || 0}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Failed</span>
                        <span className="font-extrabold text-rose-400 text-sm">{tboStatus?.failedDetailBatches || 0}</span>
                      </div>
                    </div>

                    {tboStatus?.lastErrorReason && (
                      <p className="mt-2.5 text-xs text-rose-300 bg-rose-950/60 p-2 rounded-lg border border-rose-800/60">
                        <strong>Error:</strong> {tboStatus.lastErrorReason}
                      </p>
                    )}
                  </div>
                )}

                {/* TripJack Hotel Static Content Sync Live Progress Card */}
                {scheduler.id === 'tripJackHotelStaticContentSync' && (tripJackStatus?.status === 'IN_PROGRESS' || scheduler.lastRunStatus === 'IN_PROGRESS') && (
                  <div className="mt-4 p-5 bg-slate-900 text-white rounded-xl border border-slate-800 shadow-lg">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-xs uppercase tracking-wider text-teal-400 flex items-center">
                          <span className="h-2.5 w-2.5 rounded-full bg-teal-400 inline-block mr-2 animate-ping"></span>
                          Live Execution Progress
                        </span>
                        <span className="text-xs font-mono bg-teal-950 px-2 py-0.5 rounded text-teal-300 border border-teal-700/50">
                          {tripJackStatus?.phase || 'STEP1_HOTEL_CODES'}
                        </span>
                      </div>

                      <button
                        onClick={fetchTripJackStatus}
                        className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-xs font-bold shadow transition-all duration-150 active:scale-95"
                      >
                        <svg className="w-3.5 h-3.5 animate-spin-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.5" />
                        </svg>
                        <span>See Progress / Refresh</span>
                      </button>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-800 rounded-full h-5 overflow-hidden border border-slate-700 p-0.5">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          tripJackStatus?.status === 'IN_PROGRESS' 
                            ? 'bg-gradient-to-r from-teal-500 via-emerald-500 to-cyan-400 animate-pulse' 
                            : tripJackStatus?.status === 'SUCCESS' 
                            ? 'bg-emerald-500' 
                            : 'bg-teal-600'
                        }`}
                        style={{ width: `${Math.min(100, Math.max(0, tripJackStatus?.progressPercentage || 0))}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between items-center mt-2 text-xs font-semibold">
                      <span className="text-slate-300">
                        Overall Completion: <strong className="text-teal-400 text-sm">{tripJackStatus?.progressPercentage ? tripJackStatus.progressPercentage.toFixed(1) : 0}%</strong>
                      </span>
                      <span className="text-slate-400">
                        Status: <strong className={tripJackStatus?.status === 'IN_PROGRESS' ? 'text-amber-400 animate-pulse' : 'text-teal-400'}>{tripJackStatus?.status || 'READY'}</strong>
                      </span>
                    </div>

                    {/* Phase Metrics Breakdown */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-3.5 pt-3 border-t border-slate-800 text-xs">
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Step 1 Mappings</span>
                        <span className="font-extrabold text-white text-sm">{tripJackStatus?.processedCities || 0} / {tripJackStatus?.totalCities || 0}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Step 2 Batches</span>
                        <span className="font-extrabold text-white text-sm">{tripJackStatus?.processedDetailBatches || 0} / {tripJackStatus?.totalDetailBatches || 0}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Successful</span>
                        <span className="font-extrabold text-emerald-400 text-sm">{tripJackStatus?.completedDetailBatches || 0}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Failed</span>
                        <span className="font-extrabold text-rose-400 text-sm">{tripJackStatus?.failedDetailBatches || 0}</span>
                      </div>
                    </div>

                    {tripJackStatus?.lastErrorReason && (
                      <p className="mt-2.5 text-xs text-rose-300 bg-rose-950/60 p-2 rounded-lg border border-rose-800/60">
                        <strong>Error:</strong> {tripJackStatus.lastErrorReason}
                      </p>
                    )}
                  </div>
                )}

                {/* TBO Country & City Code Sync Live Progress Card */}
                {scheduler.id === 'tboCountryCitySync' && (tboCityStatus?.status === 'IN_PROGRESS' || scheduler.lastRunStatus === 'IN_PROGRESS') && (
                  <div className="mt-4 p-5 bg-slate-900 text-white rounded-xl border border-slate-800 shadow-lg">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-xs uppercase tracking-wider text-blue-400 flex items-center">
                          <span className="h-2.5 w-2.5 rounded-full bg-blue-400 inline-block mr-2 animate-ping"></span>
                          Live Execution Progress
                        </span>
                        <span className="text-xs font-mono bg-blue-950 px-2 py-0.5 rounded text-blue-300 border border-blue-700/50">
                          {tboCityStatus?.phase || 'CITY_LIST_SYNC'}
                        </span>
                      </div>

                      <button
                        onClick={fetchTboCityStatus}
                        className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold shadow transition-all duration-150 active:scale-95"
                      >
                        <svg className="w-3.5 h-3.5 animate-spin-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.5" />
                        </svg>
                        <span>See Progress / Refresh</span>
                      </button>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-800 rounded-full h-5 overflow-hidden border border-slate-700 p-0.5">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          tboCityStatus?.status === 'IN_PROGRESS' 
                            ? 'bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-400 animate-pulse' 
                            : tboCityStatus?.status === 'SUCCESS' 
                            ? 'bg-emerald-500' 
                            : 'bg-blue-600'
                        }`}
                        style={{ width: `${Math.min(100, Math.max(0, tboCityStatus?.progressPercentage || (tboCityStatus?.status === 'SUCCESS' ? 100 : 0)))}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between items-center mt-2 text-xs font-semibold">
                      <span className="text-slate-300">
                        Overall Completion: <strong className="text-blue-400 text-sm">{tboCityStatus?.progressPercentage ? tboCityStatus.progressPercentage.toFixed(1) : (tboCityStatus?.status === 'SUCCESS' ? 100 : 0)}%</strong>
                      </span>
                      <span className="text-slate-400">
                        Status: <strong className={tboCityStatus?.status === 'IN_PROGRESS' ? 'text-amber-400 animate-pulse' : 'text-emerald-400'}>{tboCityStatus?.status || 'READY'}</strong>
                      </span>
                    </div>

                    {/* Phase Metrics Breakdown */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-3.5 pt-3 border-t border-slate-800 text-xs">
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Processed Cities</span>
                        <span className="font-extrabold text-white text-sm">{tboCityStatus?.processedCities || 0} / {tboCityStatus?.totalCities || 0}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Execution Phase</span>
                        <span className="font-extrabold text-blue-300 text-sm">{tboCityStatus?.phase || 'IDLE'}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Current Status</span>
                        <span className="font-extrabold text-emerald-400 text-sm">{tboCityStatus?.status || 'READY'}</span>
                      </div>
                    </div>

                    {tboCityStatus?.lastErrorReason && (
                      <p className="mt-2.5 text-xs text-rose-300 bg-rose-950/60 p-2 rounded-lg border border-rose-800/60">
                        <strong>Error:</strong> {tboCityStatus.lastErrorReason}
                      </p>
                    )}
                  </div>
                )}

                {/* TripJack City & Region Mapping Sync Live Progress Card */}
                {scheduler.id === 'tripJackCityRegionMappingSync' && (tripJackCityStatus?.status === 'IN_PROGRESS' || scheduler.lastRunStatus === 'IN_PROGRESS') && (
                  <div className="mt-4 p-5 bg-slate-900 text-white rounded-xl border border-slate-800 shadow-lg">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-xs uppercase tracking-wider text-cyan-400 flex items-center">
                          <span className="h-2.5 w-2.5 rounded-full bg-cyan-400 inline-block mr-2 animate-ping"></span>
                          Live Execution Progress
                        </span>
                        <span className="text-xs font-mono bg-cyan-950 px-2 py-0.5 rounded text-cyan-300 border border-cyan-700/50">
                          {tripJackCityStatus?.phase || 'REGION_CITY_MAPPING'}
                        </span>
                      </div>

                      <button
                        onClick={fetchTripJackCityStatus}
                        className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-xs font-bold shadow transition-all duration-150 active:scale-95"
                      >
                        <svg className="w-3.5 h-3.5 animate-spin-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.5" />
                        </svg>
                        <span>See Progress / Refresh</span>
                      </button>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-800 rounded-full h-5 overflow-hidden border border-slate-700 p-0.5">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          tripJackCityStatus?.status === 'IN_PROGRESS' 
                            ? 'bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-400 animate-pulse' 
                            : tripJackCityStatus?.status === 'SUCCESS' 
                            ? 'bg-emerald-500' 
                            : 'bg-cyan-600'
                        }`}
                        style={{ width: `${Math.min(100, Math.max(0, tripJackCityStatus?.progressPercentage || (tripJackCityStatus?.status === 'SUCCESS' ? 100 : 0)))}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between items-center mt-2 text-xs font-semibold">
                      <span className="text-slate-300">
                        Overall Completion: <strong className="text-cyan-400 text-sm">{tripJackCityStatus?.progressPercentage ? tripJackCityStatus.progressPercentage.toFixed(1) : (tripJackCityStatus?.status === 'SUCCESS' ? 100 : 0)}%</strong>
                      </span>
                      <span className="text-slate-400">
                        Status: <strong className={tripJackCityStatus?.status === 'IN_PROGRESS' ? 'text-amber-400 animate-pulse' : 'text-emerald-400'}>{tripJackCityStatus?.status || 'READY'}</strong>
                      </span>
                    </div>

                    {/* Phase Metrics Breakdown */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-3.5 pt-3 border-t border-slate-800 text-xs">
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Processed Mappings</span>
                        <span className="font-extrabold text-white text-sm">{tripJackCityStatus?.processedCities || 0} / {tripJackCityStatus?.totalCities || 0}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Execution Phase</span>
                        <span className="font-extrabold text-cyan-300 text-sm">{tripJackCityStatus?.phase || 'IDLE'}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Current Status</span>
                        <span className="font-extrabold text-emerald-400 text-sm">{tripJackCityStatus?.status || 'READY'}</span>
                      </div>
                    </div>

                    {tripJackCityStatus?.lastErrorReason && (
                      <p className="mt-2.5 text-xs text-rose-300 bg-rose-950/60 p-2 rounded-lg border border-rose-800/60">
                        <strong>Error:</strong> {tripJackCityStatus.lastErrorReason}
                      </p>
                    )}
                  </div>
                )}

                {/* RezLive City Sync Live Progress Card */}
                {scheduler.id === 'rezLiveCitySync' && (rezLiveCityStatus?.status === 'IN_PROGRESS' || scheduler.lastRunStatus === 'IN_PROGRESS') && (
                  <div className="mt-4 p-5 bg-slate-900 text-white rounded-xl border border-slate-800 shadow-lg">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-xs uppercase tracking-wider text-amber-400 flex items-center">
                          <span className="h-2.5 w-2.5 rounded-full bg-amber-400 inline-block mr-2 animate-ping"></span>
                          Live Execution Progress
                        </span>
                        <span className="text-xs font-mono bg-amber-950 px-2 py-0.5 rounded text-amber-300 border border-amber-700/50">
                          {rezLiveCityStatus?.phase || 'REZLIVE_CITY_IMPORT'}
                        </span>
                      </div>

                      <button
                        onClick={fetchRezLiveCityStatus}
                        className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-bold shadow transition-all duration-150 active:scale-95"
                      >
                        <svg className="w-3.5 h-3.5 animate-spin-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.5" />
                        </svg>
                        <span>See Progress / Refresh</span>
                      </button>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-800 rounded-full h-5 overflow-hidden border border-slate-700 p-0.5">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          rezLiveCityStatus?.status === 'IN_PROGRESS' 
                            ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-emerald-400 animate-pulse' 
                            : rezLiveCityStatus?.status === 'SUCCESS' 
                            ? 'bg-emerald-500' 
                            : 'bg-amber-600'
                        }`}
                        style={{ width: `${Math.min(100, Math.max(0, rezLiveCityStatus?.progressPercentage || (rezLiveCityStatus?.status === 'SUCCESS' ? 100 : 0)))}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between items-center mt-2 text-xs font-semibold">
                      <span className="text-slate-300">
                        Overall Completion: <strong className="text-amber-400 text-sm">{rezLiveCityStatus?.progressPercentage ? rezLiveCityStatus.progressPercentage.toFixed(1) : (rezLiveCityStatus?.status === 'SUCCESS' ? 100 : 0)}%</strong>
                      </span>
                      <span className="text-slate-400">
                        Status: <strong className={rezLiveCityStatus?.status === 'IN_PROGRESS' ? 'text-amber-400 animate-pulse' : 'text-emerald-400'}>{rezLiveCityStatus?.status || 'READY'}</strong>
                      </span>
                    </div>

                    {/* Phase Metrics Breakdown */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-3.5 pt-3 border-t border-slate-800 text-xs">
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Processed Rows</span>
                        <span className="font-extrabold text-white text-sm">{rezLiveCityStatus?.processedCities || 0}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Execution Phase</span>
                        <span className="font-extrabold text-amber-300 text-sm">{rezLiveCityStatus?.phase || 'IDLE'}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Current Status</span>
                        <span className="font-extrabold text-emerald-400 text-sm">{rezLiveCityStatus?.status || 'READY'}</span>
                      </div>
                    </div>

                    {rezLiveCityStatus?.lastErrorReason && (
                      <p className="mt-2.5 text-xs text-rose-300 bg-rose-950/60 p-2 rounded-lg border border-rose-800/60">
                        <strong>Error:</strong> {rezLiveCityStatus.lastErrorReason}
                      </p>
                    )}
                  </div>
                )}

                {/* RezLive Hotel Sync Live Progress Card */}
                {scheduler.id === 'rezLiveHotelSync' && (rezLiveHotelStatus?.status === 'IN_PROGRESS' || scheduler.lastRunStatus === 'IN_PROGRESS') && (
                  <div className="mt-4 p-5 bg-slate-900 text-white rounded-xl border border-slate-800 shadow-lg">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-xs uppercase tracking-wider text-orange-400 flex items-center">
                          <span className="h-2.5 w-2.5 rounded-full bg-orange-400 inline-block mr-2 animate-ping"></span>
                          Live Execution Progress
                        </span>
                        <span className="text-xs font-mono bg-orange-950 px-2 py-0.5 rounded text-orange-300 border border-orange-700/50">
                          {rezLiveHotelStatus?.phase || 'REZLIVE_HOTEL_SYNC'}
                        </span>
                      </div>

                      <button
                        onClick={fetchRezLiveHotelStatus}
                        className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-xs font-bold shadow transition-all duration-150 active:scale-95"
                      >
                        <svg className="w-3.5 h-3.5 animate-spin-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.5" />
                        </svg>
                        <span>See Progress / Refresh</span>
                      </button>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-800 rounded-full h-5 overflow-hidden border border-slate-700 p-0.5">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          rezLiveHotelStatus?.status === 'IN_PROGRESS' 
                            ? 'bg-gradient-to-r from-orange-500 via-amber-500 to-emerald-400 animate-pulse' 
                            : rezLiveHotelStatus?.status === 'SUCCESS' 
                            ? 'bg-emerald-500' 
                            : 'bg-orange-600'
                        }`}
                        style={{ width: `${Math.min(100, Math.max(0, rezLiveHotelStatus?.progressPercentage || (rezLiveHotelStatus?.status === 'SUCCESS' ? 100 : 0)))}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between items-center mt-2 text-xs font-semibold">
                      <span className="text-slate-300">
                        Overall Completion: <strong className="text-orange-400 text-sm">{rezLiveHotelStatus?.progressPercentage ? rezLiveHotelStatus.progressPercentage.toFixed(1) : (rezLiveHotelStatus?.status === 'SUCCESS' ? 100 : 0)}%</strong>
                      </span>
                      <span className="text-slate-400">
                        Status: <strong className={rezLiveHotelStatus?.status === 'IN_PROGRESS' ? 'text-amber-400 animate-pulse' : 'text-emerald-400'}>{rezLiveHotelStatus?.status || 'READY'}</strong>
                      </span>
                    </div>

                    {/* Phase Metrics Breakdown */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-3.5 pt-3 border-t border-slate-800 text-xs">
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Processed Hotels</span>
                        <span className="font-extrabold text-white text-sm">{rezLiveHotelStatus?.processedCities || 0}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Execution Phase</span>
                        <span className="font-extrabold text-orange-300 text-sm">{rezLiveHotelStatus?.phase || 'IDLE'}</span>
                      </div>
                      <div className="bg-slate-800/90 p-2.5 rounded-lg border border-slate-700/60">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Current Status</span>
                        <span className="font-extrabold text-emerald-400 text-sm">{rezLiveHotelStatus?.status || 'READY'}</span>
                      </div>
                    </div>

                    {rezLiveHotelStatus?.lastErrorReason && (
                      <p className="mt-2.5 text-xs text-rose-300 bg-rose-950/60 p-2 rounded-lg border border-rose-800/60">
                        <strong>Error:</strong> {rezLiveHotelStatus.lastErrorReason}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 space-y-3">
                <div>
                  <span className="text-gray-500 block text-xs uppercase font-bold tracking-wider">Cron Schedule</span>
                  <span className="font-mono text-gray-900 font-semibold">{scheduler.cron}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-500 block text-xs uppercase font-bold tracking-wider">Last Run Status</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                      scheduler.lastRunStatus?.startsWith('SUCCESS') 
                        ? 'bg-green-100 text-green-800' 
                        : scheduler.lastRunStatus ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {scheduler.lastRunStatus || 'No log'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block text-xs uppercase font-bold tracking-wider">Last Run Time</span>
                    <span className="text-gray-900 font-medium block truncate" title={formatDateTime(scheduler.lastRunTime)}>
                      {formatDateTime(scheduler.lastRunTime)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Cron Modal */}
      {editingScheduler && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-900 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold">Reschedule: {editingScheduler.name}</h3>
              <button 
                onClick={cancelEditCron}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSaveCron} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Cron Expression (6-field format)
                </label>
                <input
                  type="text"
                  value={newCron}
                  onChange={(e) => setNewCron(e.target.value)}
                  className="w-full font-mono px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm text-gray-900"
                  placeholder="e.g. 0 0 6 * * ?"
                  required
                />
              </div>

              {/* Cron Quick Help */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800 space-y-2">
                <p className="font-bold">Spring Cron Syntax Helper:</p>
                <p className="font-mono">Seconds  Minutes  Hours  Day-of-Month  Month  Day-of-Week</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li><span className="font-mono">0 0 * * * *</span> - Run at the start of every hour</li>
                  <li><span className="font-mono">0 0 6 * * ?</span> - Run every day at 06:00 AM</li>
                  <li><span className="font-mono">0 */5 * * * ?</span> - Run every 5 minutes</li>
                  <li>Use <span className="font-mono">?</span> for Day-of-Week if Day-of-Month is specified (and vice-versa).</li>
                </ul>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={cancelEditCron}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingCron}
                  className="px-4 py-2 border border-transparent rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm disabled:opacity-50 transition-colors"
                >
                  {savingCron ? 'Saving...' : 'Update Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSchedulers;
